// src/pages/UsagePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { useAuth } from '../auth/CognitoAuthContext';
import { GetUsageAPI } from '../utilities/constants';

function UsagePage() {
  const auth = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUsage = useCallback(async () => {
    const userSub = auth.user?.profile?.sub;
    if (!userSub) {
      setError('User identifier not found.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ sub: userSub });
      const res = await fetch(`${GetUsageAPI}?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.user?.id_token}`,
        },
      });

      if (!res.ok) {
        const errData = await res.json();
        setError(errData.message || 'Error fetching usage.');
        setLoading(false);
        return;
      }

      const data = await res.json();
      setUsage(data);
    } catch (err) {
      setError(`Failed to fetch usage: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [auth.user]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const operationRows = usage ? Object.entries(usage.byOperation || {}) : [];

  return (
    <Box sx={{ maxWidth: 900, margin: '0 auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Usage
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        {usage ? `Adobe PDF Services calls for ${usage.month}` : 'Adobe PDF Services calls this month'}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading usage...</Typography>
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && usage && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 3, flex: '1 1 200px' }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Total Adobe calls
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#004AAD' }}>
                {usage.totalCalls}
              </Typography>
            </Paper>
            <Paper sx={{ p: 3, flex: '1 1 200px' }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Estimated Adobe Document Transactions
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#004AAD' }}>
                {usage.totalTransactions}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Per Adobe's published rates (Auto-Tag: 10/page, Extract: 1 per 5 pages, Accessibility Checker: 1/call)
              </Typography>
            </Paper>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Operation</TableCell>
                  <TableCell align="right">Succeeded</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {operationRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                      No Adobe transactions recorded yet this month.
                    </TableCell>
                  </TableRow>
                )}
                {operationRows.map(([operation, counts]) => (
                  <TableRow key={operation}>
                    <TableCell>{operation}</TableCell>
                    <TableCell align="right">
                      <Chip label={counts.SUCCESS || 0} color="success" size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={counts.FAILED || 0} color="error" size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{counts.transactions || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

export default UsagePage;
